import { z } from "zod";
import { assetTreeSearchSchema } from "./assetTreeSearch";
import {
  mergeRequestsPageView,
  parseRequestsPageView,
  type RequestsPageView,
} from "./requestsPageView";
import {
  type AssetId,
  assetIdSchema,
  type WorkOrderId,
  type WorkRequestId,
  workOrderIdSchema,
  workRequestIdSchema,
} from "./workIds";
import { workOrdersSearchSchema } from "./workOrdersSearch";

/**
 * Cross-module navigation origin: the record a user came from when they
 * followed a link into another module (for example Request → Work Order).
 * It travels in the destination URL so the destination can render a
 * contextual back link. Only identifiers and the origin list's view state
 * are carried; display labels are always resolved from the API, never
 * trusted from the URL.
 */

const workOrdersOriginSearchSchema = workOrdersSearchSchema
  .strip()
  .omit({ workOrderFullPage: true, workOrderSelectedId: true });

const assetTreeOriginSearchSchema = assetTreeSearchSchema
  .strip()
  .omit({ assetOpenId: true });

export type RequestsOriginSearch = Omit<RequestsPageView, "request">;
export type WorkOrdersOriginSearch = z.infer<
  typeof workOrdersOriginSearchSchema
>;
export type AssetTreeOriginSearch = z.infer<typeof assetTreeOriginSearchSchema>;

/**
 * A record the user came from. `parent` is the origin that page was itself
 * opened from, so contextual Back can walk back through several modules
 * (Request, Asset, Work Order). The chain is bounded by
 * `MAX_ORIGIN_DEPTH` and never repeats a record.
 */
export type NavigationOrigin =
  | {
      type: "request";
      id: WorkRequestId;
      search?: RequestsOriginSearch;
      parent?: NavigationOrigin;
    }
  | {
      type: "workOrder";
      id: WorkOrderId;
      search?: WorkOrdersOriginSearch;
      parent?: NavigationOrigin;
    }
  | {
      type: "asset";
      id: AssetId;
      search?: AssetTreeOriginSearch;
      parent?: NavigationOrigin;
    };

export type NavigationOriginType = NavigationOrigin["type"];

export type NavigationOriginSearch = {
  originType?: NavigationOriginType;
  originId?: string;
  originSearch?:
    | RequestsOriginSearch
    | WorkOrdersOriginSearch
    | AssetTreeOriginSearch;
  originParent?: NavigationOrigin;
};

/** Longest origin chain kept in a URL: the origin plus its ancestors. */
export const MAX_ORIGIN_DEPTH = 3;

function originKey(origin: { type: string; id: string }): string {
  return `${origin.type}:${origin.id}`;
}

const originTypeSchema = z.enum(["request", "workOrder", "asset"]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseRequestsOriginSearch(
  value: unknown,
): RequestsOriginSearch | undefined {
  if (!isRecord(value)) return undefined;
  const { request: _request, ...view } = mergeRequestsPageView(
    parseRequestsPageView(value),
    {},
  );
  return Object.keys(view).length > 0 ? view : undefined;
}

function parseWithSchema<Schema extends z.ZodTypeAny>(
  schema: Schema,
  value: unknown,
): z.infer<Schema> | undefined {
  if (!isRecord(value)) return undefined;
  const parsed = schema.safeParse(value);
  return parsed.success ? (parsed.data as z.infer<Schema>) : undefined;
}

/**
 * Reads an origin from a route's search. Never throws: an unknown type or a
 * malformed id yields no origin, and a malformed origin list search keeps
 * the origin but drops its list state. Links written before the generic
 * keys existed (`originWorkOrderId` / `originWorkOrderSearch`) are read as a
 * Work Order origin.
 */
export function parseNavigationOrigin(
  search: Record<string, unknown> | undefined,
): NavigationOrigin | undefined {
  if (!search) return undefined;
  if (search.originType === undefined) {
    const legacyId = workOrderIdSchema.safeParse(search.originWorkOrderId);
    if (!legacyId.success) return undefined;
    return withSearch({
      type: "workOrder",
      id: legacyId.data,
      search: parseWithSchema(
        workOrdersOriginSearchSchema,
        search.originWorkOrderSearch,
      ),
    });
  }

  const base = parseOriginParts(
    search.originType,
    search.originId,
    search.originSearch,
  );
  if (!base) return undefined;
  const parent = parseParentOrigin(
    search.originParent,
    [originKey(base)],
    MAX_ORIGIN_DEPTH - 1,
  );
  return parent ? { ...base, parent } : base;
}

/** One origin record without its parent chain. */
function parseOriginParts(
  rawType: unknown,
  rawId: unknown,
  rawSearch: unknown,
): NavigationOrigin | undefined {
  const type = originTypeSchema.safeParse(rawType);
  if (!type.success) return undefined;
  switch (type.data) {
    case "request": {
      const id = workRequestIdSchema.safeParse(rawId);
      return id.success
        ? withSearch({
            type: "request",
            id: id.data,
            search: parseRequestsOriginSearch(rawSearch),
          })
        : undefined;
    }
    case "workOrder": {
      const id = workOrderIdSchema.safeParse(rawId);
      return id.success
        ? withSearch({
            type: "workOrder",
            id: id.data,
            search: parseWithSchema(workOrdersOriginSearchSchema, rawSearch),
          })
        : undefined;
    }
    case "asset": {
      const id = assetIdSchema.safeParse(rawId);
      return id.success
        ? withSearch({
            type: "asset",
            id: id.data,
            search: parseWithSchema(assetTreeOriginSearchSchema, rawSearch),
          })
        : undefined;
    }
  }
}

/**
 * Reads the chain of origins behind an origin. A malformed entry, a record
 * already in the chain, or an entry past the depth cap ends the chain there
 * (the entries before it are kept).
 */
function parseParentOrigin(
  value: unknown,
  seen: string[],
  remaining: number,
): NavigationOrigin | undefined {
  if (remaining <= 0 || !isRecord(value)) return undefined;
  const base = parseOriginParts(value.type, value.id, value.search);
  if (!base || seen.includes(originKey(base))) return undefined;
  const parent = parseParentOrigin(
    value.parent,
    [...seen, originKey(base)],
    remaining - 1,
  );
  return parent ? { ...base, parent } : base;
}

/** Drops empty or missing list state so it isn't written to the URL. */
function withSearch<T extends NavigationOrigin>(origin: T): T {
  if (!origin.search) {
    const { search: _search, ...rest } = origin;
    return rest as T;
  }
  const entries = Object.entries(origin.search).filter(
    ([, value]) => value !== undefined,
  );
  if (entries.length === 0) {
    const { search: _search, ...rest } = origin;
    return rest as T;
  }
  return { ...origin, search: Object.fromEntries(entries) };
}

/**
 * An origin as a link site describes it. `search` may be the origin page's
 * whole route search: it is reduced to that module's list view state (scope,
 * selection and unrelated keys are dropped) before it is written to the URL.
 */
export type NavigationOriginLink =
  | {
      type: "request";
      id: WorkRequestId;
      search?: object;
      parent?: NavigationOrigin;
    }
  | {
      type: "workOrder";
      id: WorkOrderId;
      search?: object;
      parent?: NavigationOrigin;
    }
  | { type: "asset"; id: AssetId; search?: object; parent?: NavigationOrigin };

/** Ends the chain before the record it is about to link to, so Back never loops. */
function truncateAt(
  origin: NavigationOrigin | undefined,
  destination: { type: string; id: string },
): NavigationOrigin | undefined {
  if (!origin || originKey(origin) === originKey(destination)) return undefined;
  const parent = truncateAt(origin.parent, destination);
  const { parent: _parent, ...rest } = origin;
  return parent ? { ...rest, parent } : rest;
}

/**
 * URL search for a link that should show a back link to `origin`. Pass the
 * record being linked to as `destination` so a chain that already contains it
 * is cut there instead of sending Back around in a loop.
 */
export function toOriginSearch(
  origin: NavigationOriginLink,
  destination?: { type: NavigationOriginType; id: string },
): NavigationOriginSearch {
  const normalized = parseNavigationOrigin({
    originType: origin.type,
    originId: origin.id,
    originSearch: origin.search,
    originParent: origin.parent,
  });
  if (!normalized) return {};
  const parent = destination
    ? truncateAt(normalized.parent, destination)
    : normalized.parent;
  return {
    originType: normalized.type,
    originId: normalized.id,
    ...(normalized.search ? { originSearch: normalized.search } : {}),
    ...(parent ? { originParent: parent } : {}),
  };
}
